import { defineStore } from 'pinia'

import { useShelterStore } from '@/stores/shelterStore'
import { useTargetStore } from '@/stores/targetStore'

function normalizeSearchQuery(value) {
  return (value || '').trim().toLocaleLowerCase()
}

function splitSearchTerms(query) {
  if (!query) {
    return []
  }

  return query.split(/\s+/).filter(Boolean)
}

function escapeRegExp(value) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

function buildSearchPattern(query) {
  const searchTerms = splitSearchTerms(query)

  if (searchTerms.length === 0) {
    return null
  }

  return new RegExp(searchTerms.map(escapeRegExp).join('|'), 'i')
}

function matchesAddress(value, query) {
  const searchPattern = buildSearchPattern(query)

  if (!searchPattern) {
    return true
  }

  return searchPattern.test(String(value || ''))
}

function matchesAnyField(values, query) {
  const searchPattern = buildSearchPattern(query)

  if (!searchPattern) {
    return true
  }

  return values.some((value) => searchPattern.test(String(value || '')))
}

function hasCoordinates(...values) {
  return values.every((value) => value !== null && value !== undefined)
}

function hasUsableCoordinates(latitudeLike, longitudeLike) {
  if (!hasCoordinates(latitudeLike, longitudeLike)) {
    return false
  }

  return !(Number(latitudeLike) === 0 && Number(longitudeLike) === 0)
}

export const useSearchStore = defineStore('search', {
  state: () => ({
    searchQuery: '',
    buildingCodeQuery: '',
    selectedBuildingId: null,
    selectedTargetId: null
  }),
  getters: {
    normalizedSearchQuery(state) {
      return normalizeSearchQuery(state.searchQuery)
    },
    normalizedBuildingCodeQuery(state) {
      return normalizeSearchQuery(state.buildingCodeQuery)
    },
    filteredBuildings() {
      const shelterStore = useShelterStore()
      const buildings = shelterStore.$state.shelters || []

      return buildings.filter((building) => {
        return hasUsableCoordinates(building.gps_lat, building.gps_long)
          && matchesAddress(building.name_address, this.normalizedSearchQuery)
          && matchesAddress(building.building_code, this.normalizedBuildingCodeQuery)
      })
    },
    filteredTargets() {
      const targetStore = useTargetStore()
      const targets = targetStore.$state.targets || []

      return targets.filter((target) => {
        return hasUsableCoordinates(target.x, target.y)
          && matchesAnyField([target.address, target.name], this.normalizedSearchQuery)
          && matchesAnyField([target.address, target.name], this.normalizedBuildingCodeQuery)
      })
    },
    filteredBuildingIds() {
      return this.filteredBuildings
        .map((building) => building.id)
        .filter((buildingId) => buildingId != null)
    },
    filteredTargetIds() {
      return this.filteredTargets
        .map((target) => target.id)
        .filter((targetId) => targetId != null)
    },
    visibleCapacityTotals() {
      return this.filteredBuildings.reduce((totals, building) => {
        totals.total_n_k += Number(building.total_n_k || 0)
        totals.total_n_ks += Number(building.total_n_ks || 0)
        totals.total_n_kd += Number(building.total_n_kd || 0)
        return totals
      }, {
        total_n_k: 0,
        total_n_ks: 0,
        total_n_kd: 0
      })
    },
    selectedBuilding(state) {
      return this.filteredBuildings.find((building) => building.id === state.selectedBuildingId) || null
    },
    selectedTarget(state) {
      return this.filteredTargets.find((target) => target.id === state.selectedTargetId) || null
    }
  },
  actions: {
    setSearchQuery(value) {
      this.searchQuery = value || ''
    },
    setBuildingCodeQuery(value) {
      this.buildingCodeQuery = value || ''
    },
    setSelectedBuildingId(buildingId) {
      this.selectedBuildingId = buildingId ?? null
    },
    clearSelectedBuilding() {
      this.selectedBuildingId = null
    },
    setSelectedTargetId(targetId) {
      this.selectedTargetId = targetId ?? null
    },
    clearSelectedTarget() {
      this.selectedTargetId = null
    }
  }
})